import { setApiBaseUrl } from 'actions/appConfiguration';
import { Employee } from 'api-client/models/Employee';
import ErrorBoundary from 'components/Error/ErrorBoundary';
import { history } from 'config/history';
import { browserRoutes, hashRoutes } from 'constants/routes';
import { ConsentContext } from 'contexts/ConsentContext';
import useConsent from 'hooks/useConsent';
import useSelector from 'hooks/useSelector';
import MyLearningPage from 'pages/MyLearning/MyLearningPage';
import ArticleList from 'pages/Zendesk/ArticleList/ArticleList';
import CategoryList from 'pages/Zendesk/Category/CategoryList';
import SectionList from 'pages/Zendesk/Section/SectionList';
import React, { lazy, Suspense } from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
    BrowserRouter,
    HashRouter,
    Redirect,
    Route,
    Router,
    Switch,
    useLocation
} from 'react-router-dom';
import en from 'static/translations/en.json';
import { Locale } from 'types/I18n';

import { ACTIONS } from './actions/user/types';
import Layout from './Layout';
import HomePage from './pages/Home';

const MyTeam = lazy(() => import("pages/MyTeam"));
const ContentItem = lazy(() => import("pages/Zendesk/Article/Article"));
const Profile = lazy(() => import("pages/Profile/Profile"));
const messages = {
    en,
};
const BrowserRoutes = () => {
    const { pathname } = useLocation();
    return (
        <Suspense fallback={<div />}>
            <Switch>
                <Redirect from="/:content_id*(/+)" to={pathname.slice(0, -1)} />
                <Route exact={true} path={`/${browserRoutes.home}`}>
                    <HomePage />
                </Route>
                <Route path={`/${browserRoutes.myTeam}`}>
                    <MyTeam />
                </Route>
                <Route path={`/${browserRoutes.myLearning}`}>
                    <MyLearningPage />
                </Route>
                <Route path={`/${browserRoutes.profile}`}>
                    <Profile isExternal={false} />
                </Route>
                <Route exact={true} path={`/${browserRoutes.collection}`}>
                    <CategoryList />
                </Route>
                <Route path={`/${browserRoutes.collection}/:categorySlug`}>
                    <SectionList />
                    <Route path={`/${browserRoutes.collection}/:categorySlug/:sectionSlug`}>
                        <ArticleList />
                    </Route>
                </Route>
            </Switch>
        </Suspense>
    );
};
const HashRoutes = () => {
    return (
        <HashRouter hashType="noslash">
            <Suspense fallback={<div />}>
                <Switch>
                    <Route path={`/${hashRoutes.contentItem}/:articleId`}>
                        <ContentItem />
                    </Route>
                </Switch>
            </Suspense>
        </HashRouter>
    );
};
interface AppProps {
    profile: Employee;
    apiBaseUrl: string;
}

export const App: React.FC<AppProps> = ({ apiBaseUrl, profile }) => {
    const selectedLocale = Locale.en;
    const { isOpen, reOpen, onContinue } = useConsent();
    const dispatch = useDispatch();
    React.useEffect(() => {
        dispatch({
            type: ACTIONS.GET_USER_PROFILE_SUCCESS,
            payload: profile,
        });
        dispatch(setApiBaseUrl(apiBaseUrl));
    }, []);
    const reduxProfile = useSelector((state) => state.user.profile?.data);
    const reduxApiBaseUrl = useSelector((state) => state.appConfiguration.apiBaseUrl);
    if (!reduxProfile || !reduxApiBaseUrl) {
        return null;
    }
    return (
        <ConsentContext.Provider
            value={{
                isOpen,
                onContinue,
                reOpen,
            }}
        >
                <IntlProvider locale={selectedLocale} messages={messages[selectedLocale]}>
                    <BrowserRouter>
                        <Switch>
                            <Route path="/">
                                <Router history={history}>
                                    <Layout>
                                        <ErrorBoundary>
                                            <BrowserRoutes />
                                        </ErrorBoundary>
                                    </Layout>
                                    <ErrorBoundary>
                                        <HashRoutes />
                                    </ErrorBoundary>
                                </Router>
                            </Route>
                        </Switch>
                    </BrowserRouter>
                </IntlProvider>
        </ConsentContext.Provider>
    );
};
